import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useMemo, useState } from "react";
import { formatDateShort } from "../../lib/format";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { tmApi, type TmProjectView, type TmTagView, type TmTaskPriority, type TmTaskStatus, type TmTaskView } from "../../api/tm";
import { TmStatusBadge } from "../../components/tm/TmStatusBadge";
import { TmPriorityPill } from "../../components/tm/TmPriorityPill";
import { useToast } from "../../components/Toast";
import type { ApiError } from "../../api/client";
import Ic from "../../components/icons.jsx";

// Client-side mirror of TmTaskStatus FSM (see backend TmTaskStatus.java).
// Used to grey out invalid drop targets — BE remains the source of truth and
// will reject anything that slips past the UI check.
const ALLOWED_NEXT: Record<TmTaskStatus, TmTaskStatus[]> = {
  TODO:        ["IN_PROGRESS", "BLOCKED", "CANCELLED"],
  IN_PROGRESS: ["IN_REVIEW", "BLOCKED", "CANCELLED", "TODO"],
  IN_REVIEW:   ["DONE", "IN_PROGRESS", "CANCELLED"],
  BLOCKED:     ["TODO", "IN_PROGRESS", "CANCELLED"],
  DONE:        ["IN_PROGRESS"],
  CANCELLED:   [],
};

type Tab = "mine" | "assigned" | "validation";
type View = "kanban" | "list";

const COLUMNS: TmTaskStatus[] = ["TODO", "IN_PROGRESS", "IN_REVIEW", "DONE", "BLOCKED"];
const PRIORITIES: TmTaskPriority[] = ["URGENT", "HIGH", "MEDIUM", "LOW"];

/**
 * Tasks home — three tabs (mine / assigned by me / for my validation) over a
 * Kanban-style board so the assignee can scan their queue at a glance.
 *
 * KPI strip surfaces work that needs action today: counts per status plus an
 * overdue total. Filters (search, priority, overdue-only) apply across the
 * whole board. List view stays for users who prefer a denser one-line view.
 */
export default function TmTasksPage() {
  const { canWrite } = useSectionAccess("tm_tasks");
  const { t } = useTranslation();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("mine");
  const [view, setView] = useState<View>("kanban");
  const [tasks, setTasks] = useState<TmTaskView[] | null>(null);
  const [counts, setCounts] = useState<Record<Tab, number>>({ mine: 0, assigned: 0, validation: 0 });
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<Set<TmTaskPriority>>(new Set());
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [allTags, setAllTags] = useState<TmTagView[]>([]);
  const [projectFilter, setProjectFilter] = useState<Set<string>>(new Set());
  const [allProjects, setAllProjects] = useState<TmProjectView[]>([]);
  const [draggingTask, setDraggingTask] = useState<TmTaskView | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  async function refresh() {
    const [mine, assigned, validation] = await Promise.all([
      tmApi.mine(), tmApi.assignedByMe(), tmApi.forValidation(),
    ]);
    setCounts({ mine: mine.length, assigned: assigned.length, validation: validation.length });
    setTasks(tab === "mine" ? mine : tab === "assigned" ? assigned : validation);
  }

  useEffect(() => { void refresh(); setSelectedIds(new Set()); }, [tab]);
  useEffect(() => { void tmApi.listTags().then(setAllTags).catch(() => setAllTags([])); }, []);
  useEffect(() => { void tmApi.listProjects().then(setAllProjects).catch(() => setAllProjects([])); }, []);

  function toggleSelect(taskId: string) {
    if (!canWrite) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  }

  /**
   * Bulk transition — apply the same target status to every selected task,
   * skipping ones the FSM rejects. We report a single toast with the
   * (succeeded / skipped) split so the user knows what happened without
   * scrolling a long error stack.
   */
  async function bulkTransition(toStatus: TmTaskStatus) {
    if (!canWrite || !tasks || selectedIds.size === 0) return;
    const selected = tasks.filter((t) => selectedIds.has(t.id));
    setBulkBusy(true);
    let ok = 0;
    let skipped = 0;
    const failed: string[] = [];
    for (const task of selected) {
      if (task.status === toStatus) { skipped++; continue; }
      if (!ALLOWED_NEXT[task.status]?.includes(toStatus)) { skipped++; continue; }
      try {
        await tmApi.transition(task.id, { toStatus });
        ok++;
      } catch (_err) {
        failed.push(task.title);
      }
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    await refresh();
    const parts: string[] = [];
    if (ok > 0)              parts.push(t("tm.tasks.bulk.moved", { defaultValue: "{{count}} moved to {{status}}", count: ok, status: t(`tm.status.${toStatus}`, { defaultValue: humanStatus(toStatus) }) }));
    if (skipped > 0)         parts.push(t("tm.tasks.bulk.skipped", { defaultValue: "{{count}} skipped (invalid transition)", count: skipped }));
    if (failed.length > 0)   parts.push(t("tm.tasks.bulk.failed", { defaultValue: "{{count}} failed", count: failed.length }));
    if (failed.length > 0)   toast.error(parts.join(" · "));
    else                     toast.success(parts.join(" · "));
  }

  async function bulkSetPriority(priority: TmTaskPriority) {
    if (!canWrite || !tasks || selectedIds.size === 0) return;
    setBulkBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const id of selectedIds) {
      try {
        await tmApi.update(id, { priority });
        ok++;
      } catch (_err) {
        const t = tasks.find((tk) => tk.id === id);
        failed.push(t?.title ?? id);
      }
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    await refresh();
    if (failed.length > 0) toast.error(t("tm.tasks.bulk.priorityResult", { defaultValue: "{{ok}} updated · {{failed}} failed", ok, failed: failed.length }));
    else                   toast.success(t("tm.tasks.bulk.prioritySet", { defaultValue: "{{count}} task(s) set to {{priority}}.", count: ok, priority: t(`tm.priority.${priority}`, { defaultValue: priorityLabel(priority) }) }));
  }

  /**
   * Optimistic D&D move. We mutate local state first so the card jumps to the
   * target column immediately, then fire the BE transition. On failure we
   * roll back and refetch — BE owns the validator-only and comment-required
   * checks that we can't enforce from the FE alone.
   */
  async function moveTaskTo(task: TmTaskView, toStatus: TmTaskStatus) {
    if (!canWrite) return;
    // Clear drag state up front: the optimistic re-render below unmounts the
    // dragged card before its onDragEnd fires, which would otherwise leave the
    // card stuck at the dragging opacity (0.5) until a manual refresh.
    setDraggingTask(null);
    if (task.status === toStatus) return;
    // FE-side FSM check — saves a round-trip for obviously invalid drops.
    if (!ALLOWED_NEXT[task.status]?.includes(toStatus)) {
      toast.error(t("tm.tasks.bulk.invalidMove", { defaultValue: "Can't move {{from}} → {{to}}.", from: t(`tm.status.${task.status}`, { defaultValue: humanStatus(task.status) }), to: t(`tm.status.${toStatus}`, { defaultValue: humanStatus(toStatus) }) }));
      return;
    }
    setTasks((prev) => prev?.map((tk) => tk.id === task.id ? { ...tk, status: toStatus } : tk) ?? null);
    try {
      await tmApi.transition(task.id, { toStatus });
      // Refetch in the background to capture server-set fields (startedAt,
      // completedAt) and re-derive the tab counts.
      void refresh();
    } catch (err) {
      toast.error((err as ApiError).message);
      setTasks((prev) => prev?.map((tk) => tk.id === task.id ? { ...tk, status: task.status } : tk) ?? null);
    }
  }

  const today = useMemo(() => new Date(new Date().toDateString()), []);
  const isOverdue = (task: TmTaskView) =>
    !!task.dueDate
    && new Date(task.dueDate) < today
    && task.status !== "DONE"
    && task.status !== "CANCELLED";

  const filtered = useMemo(() => {
    if (!tasks) return [];
    const q = search.trim().toLowerCase();
    return tasks.filter((task) => {
      if (priorityFilter.size > 0 && !priorityFilter.has(task.priority)) return false;
      if (overdueOnly && !isOverdue(task)) return false;
      if (projectFilter.size > 0) {
        // OR semantics: task qualifies if it belongs to any of the picked projects.
        if (!task.projectId || !projectFilter.has(task.projectId)) return false;
      }
      if (tagFilter.size > 0) {
        const have = new Set(task.tags.map((tg) => tg.id));
        // AND semantics: task must have *every* selected tag.
        for (const id of tagFilter) if (!have.has(id)) return false;
      }
      if (q) {
        const hay = `${task.title} ${task.assigneeName} ${task.projectName ?? ""} ${task.tags.map((tg) => tg.name).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, search, priorityFilter, overdueOnly, tagFilter, projectFilter, today]);

  const statusCounts = useMemo(() => {
    const c: Record<TmTaskStatus | "OVERDUE", number> = {
      TODO: 0, IN_PROGRESS: 0, IN_REVIEW: 0, DONE: 0, BLOCKED: 0, CANCELLED: 0, OVERDUE: 0,
    };
    (tasks ?? []).forEach((task) => {
      c[task.status] = (c[task.status] ?? 0) + 1;
      if (isOverdue(task)) c.OVERDUE++;
    });
    return c;
  }, [tasks, today]);

  const togglePriority = (p: TmTaskPriority) => {
    setPriorityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };
  const clearAll = () => {
    setSearch("");
    setPriorityFilter(new Set());
    setOverdueOnly(false);
    setTagFilter(new Set());
    setProjectFilter(new Set());
  };
  const filtersActive = !!search || priorityFilter.size > 0 || overdueOnly || tagFilter.size > 0 || projectFilter.size > 0;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>
            {t("tm.tasks.title", "Tasks")}
          </h1>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4 }}>
            {tasks === null
              ? t("common.loading", "Loading…")
              : `${tasks.length} ${t("tm.tasks.inView", "in this view")}${statusCounts.OVERDUE > 0 ? ` · ${statusCounts.OVERDUE} ${t("tm.tasks.detail.overdue", "Overdue").toLowerCase()}` : ""}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="btn"
            onClick={() => downloadTasksCsv(filtered, t)}
            disabled={filtered.length === 0}
            title={t("tm.tasks.exportCsvHint", "Download the currently filtered tasks as CSV")}
          >
            <Ic.download /> {t("tm.tasks.exportCsv", "Export CSV")}
          </button>
          {canWrite && <Link to="/tm/tasks/new" className="btn primary" style={{ textDecoration: "none" }}>
            <Ic.plus /> {t("tm.tasks.new", "New task")}
          </Link>}
        </div>
      </div>

      <div style={tabBar}>
        {(["mine", "assigned", "validation"] as Tab[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            style={{
              padding: "8px 14px",
              border: "none",
              borderBottom: tab === k ? "2px solid var(--brand-primary)" : "2px solid transparent",
              background: "transparent",
              color: tab === k ? "var(--ink)" : "var(--ink-3)",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {t(`tm.tasks.tab.${k}`, { defaultValue: tabLabel(k) })}
            <span style={tabCount}>{counts[k]}</span>
          </button>
        ))}
      </div>

      <div style={kpiGrid}>
        <KpiTile label={t("tm.status.TODO", "To do")} value={statusCounts.TODO} accent="var(--ink-3)" bg="var(--surface-muted)" />
        <KpiTile label={t("tm.status.IN_PROGRESS", "In progress")} value={statusCounts.IN_PROGRESS} accent="var(--info)" bg="var(--info-bg)" />
        <KpiTile label={t("tm.status.IN_REVIEW", "In review")} value={statusCounts.IN_REVIEW} accent="var(--warn)" bg="var(--warn-bg)" />
        <KpiTile label={t("tm.status.BLOCKED", "Blocked")} value={statusCounts.BLOCKED} accent="var(--err)" bg="var(--err-bg)" />
        <KpiTile
          label={t("tm.tasks.detail.overdue", "Overdue")}
          value={statusCounts.OVERDUE}
          accent="var(--err)"
          bg="var(--err-bg)"
          danger={statusCounts.OVERDUE > 0}
          onClick={() => setOverdueOnly((v) => !v)}
          active={overdueOnly}
        />
      </div>

      <div style={filterBar}>
        <div style={searchWrap}>
          <span className="ord-search-icon" style={searchIcon}><Ic.search /></span>
          <input
            className="input"
            placeholder={t("tm.tasks.searchPlaceholder", "Search title, assignee, project…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 32, height: 36, width: "100%" }}
          />
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("tm.tasks.filterPriority", "Priority:")}</span>
          {PRIORITIES.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => togglePriority(p)}
              style={priorityChip(p, priorityFilter.has(p))}
            >
              {t(`tm.priority.${p}`, { defaultValue: priorityLabel(p) })}
            </button>
          ))}
        </div>
        {allProjects.length > 0 && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("tm.tasks.filterProject", "Project:")}</span>
            {allProjects.map((p) => {
              const active = projectFilter.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProjectFilter((prev) => {
                    const next = new Set(prev);
                    if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                    return next;
                  })}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 999,
                    border: `1px solid ${active ? "var(--brand-primary)" : "var(--border)"}`,
                    background: active ? "var(--brand-primary)" : "var(--surface)",
                    color: active ? "var(--on-primary)" : "var(--ink-2)",
                    fontSize: 11, fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        )}
        {allTags.length > 0 && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("tm.tasks.filterTags", "Tags:")}</span>
            {allTags.map((tag) => {
              const active = tagFilter.has(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => setTagFilter((prev) => {
                    const next = new Set(prev);
                    if (next.has(tag.id)) next.delete(tag.id); else next.add(tag.id);
                    return next;
                  })}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: active ? `color-mix(in srgb, ${tag.color ?? "var(--brand-primary)"} 18%, var(--surface))` : "transparent",
                    color: active ? "var(--ink)" : "var(--ink-3)",
                    border: `1px solid ${active ? (tag.color ?? "var(--brand-primary)") : "var(--border)"}`,
                    padding: "4px 10px",
                    borderRadius: 999,
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className={view === "kanban" ? "btn primary sm" : "btn sm"}
            onClick={() => setView("kanban")}
            style={view !== "kanban" ? { textDecoration: "none" } : undefined}
          >
            <Ic.grid /> {t("tm.view.board", "Board")}
          </button>
          <button
            type="button"
            className={view === "list" ? "btn primary sm" : "btn sm"}
            onClick={() => setView("list")}
          >
            <Ic.list /> {t("tm.view.list", "List")}
          </button>
          {filtersActive && (
            <button type="button" className="btn sm" onClick={clearAll}>{t("action.clear", "Clear")}</button>
          )}
        </div>
      </div>

      {canWrite && view === "list" && selectedIds.size > 0 && (
        <div style={bulkBar}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
            <b style={{ color: "var(--ink)" }}>{t("tm.tasks.bulkSelected", { defaultValue: "{{count}} selected", count: selectedIds.size })}</b>
            <button
              type="button"
              className="btn sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkBusy}
            >
              {t("action.clear", "Clear")}
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("tm.status.moveTo", "Move to")}:</span>
            {COLUMNS.map((s) => (
              <button
                key={s}
                type="button"
                className="btn sm"
                disabled={bulkBusy}
                onClick={() => void bulkTransition(s)}
              >
                {t(`tm.status.${s}`, { defaultValue: humanStatus(s) })}
              </button>
            ))}
            <span style={{ width: 1, height: 22, background: "var(--border)", margin: "0 4px" }} />
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("tm.tasks.filterPriority", "Priority:")}</span>
            {PRIORITIES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => void bulkSetPriority(p)}
                disabled={bulkBusy}
                style={priorityChip(p, false)}
              >
                {t(`tm.priority.${p}`, { defaultValue: priorityLabel(p) })}
              </button>
            ))}
          </div>
        </div>
      )}

      {tasks === null ? (
        <Muted>{t("common.loading", "Loading…")}</Muted>
      ) : filtered.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>✅</div>
          <b style={{ color: "var(--ink-2)" }}>
            {tasks.length === 0
              ? t("tm.tasks.empty.tab", "Nothing in this tab yet.")
              : t("tm.tasks.empty.noMatch", "No tasks match these filters.")}
          </b>
          <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
            {tasks.length === 0
              ? t("tm.tasks.empty.createHint", "Create a task or wait for one to be assigned to you.")
              : t("tm.tasks.empty.clearHint", "Try clearing the filters.")}
          </div>
          {tasks.length === 0 ? (
            canWrite && <Link to="/tm/tasks/new" className="btn primary" style={{ textDecoration: "none", marginTop: 12 }}>
              <Ic.plus /> {t("tm.tasks.new", "New task")}
            </Link>
          ) : (
            <button type="button" className="btn sm" onClick={clearAll} style={{ marginTop: 12 }}>{t("tm.tasks.clearFilters", "Clear filters")}</button>
          )}
        </div>
      ) : view === "kanban" ? (
        <KanbanBoard
          tasks={filtered}
          today={today}
          draggingTask={draggingTask}
          onDragStart={setDraggingTask}
          onDragEnd={() => setDraggingTask(null)}
          onDrop={moveTaskTo}
        />
      ) : (
        <TaskList
          tasks={filtered}
          today={today}
          selectedIds={selectedIds}
          onToggle={toggleSelect}
          onToggleAll={() => {
            if (!canWrite) return;
            const allIds = filtered.map((t) => t.id);
            const allSelected = allIds.every((id) => selectedIds.has(id));
            setSelectedIds(allSelected ? new Set() : new Set(allIds));
          }}
        />
      )}
    </div>
  );
}

function KanbanBoard({
  tasks, today, draggingTask, onDragStart, onDragEnd, onDrop,
}: {
  tasks: TmTaskView[];
  today: Date;
  draggingTask: TmTaskView | null;
  onDragStart: (t: TmTaskView) => void;
  onDragEnd: () => void;
  onDrop: (task: TmTaskView, toStatus: TmTaskStatus) => void;
}) {
  const { canWrite } = useSectionAccess("tm_tasks");
  return (
    <div style={kanbanGrid}>
      {COLUMNS.map((status) => {
        const colTasks = tasks.filter((t) => t.status === status);
        const canDropHere =
          canWrite && !!draggingTask
          && draggingTask.status !== status
          && ALLOWED_NEXT[draggingTask.status]?.includes(status);
        const dragHinted = !!draggingTask && draggingTask.status !== status;
        return (
          <KanbanColumn
            key={status}
            status={status}
            count={colTasks.length}
            canDrop={canDropHere}
            dragHinted={dragHinted}
            onDropTask={() => draggingTask && onDrop(draggingTask, status)}
          >
            {colTasks.length === 0 ? (
              <div style={emptyCol}>—</div>
            ) : (
              colTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  today={today}
                  dragging={draggingTask?.id === task.id}
                  onDragStart={() => onDragStart(task)}
                  onDragEnd={onDragEnd}
                />
              ))
            )}
          </KanbanColumn>
        );
      })}
    </div>
  );
}

function KanbanColumn({
  status, count, canDrop, dragHinted, onDropTask, children,
}: {
  status: TmTaskStatus;
  count: number;
  canDrop: boolean;
  dragHinted: boolean;
  onDropTask: () => void;
  children: React.ReactNode;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      style={{
        ...kanbanCol,
        ...(dragHinted && !canDrop ? { opacity: 0.55 } : {}),
        ...(canDrop && over ? { borderColor: "var(--brand-primary)", background: "var(--brand-primary-50)" } : {}),
      }}
      onDragOver={(e) => {
        if (!canDrop) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!over) setOver(true);
      }}
      onDragLeave={(e) => {
        // Only flip "over" off when we actually leave the column box, not a child.
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (canDrop) onDropTask();
      }}
    >
      <header style={kanbanHead}>
        <TmStatusBadge status={status} />
        <span style={{ color: "var(--ink-4)", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
          {count}
        </span>
      </header>
      <div style={kanbanList}>{children}</div>
    </div>
  );
}

function TaskCard({
  task, today, dragging, onDragStart, onDragEnd,
}: {
  task: TmTaskView;
  today: Date;
  dragging?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const { canWrite } = useSectionAccess("tm_tasks");
  const { t } = useTranslation();
  const overdue =
    !!task.dueDate
    && new Date(task.dueDate) < today
    && task.status !== "DONE"
    && task.status !== "CANCELLED";
  return (
    <Link to={`/tm/tasks/${task.id}`} style={{ textDecoration: "none", color: "inherit" }}>
      <article
        draggable={canWrite}
        onDragStart={(e) => {
          // Some browsers need at least one type set or the drag is cancelled.
          e.dataTransfer.setData("text/plain", task.id);
          e.dataTransfer.effectAllowed = "move";
          onDragStart?.();
        }}
        onDragEnd={onDragEnd}
        style={{
          ...taskCard,
          ...(overdue ? { boxShadow: "inset 3px 0 0 var(--err), var(--shadow-1)" } : {}),
          ...(dragging ? { opacity: 0.5, transform: "scale(0.98)" } : {}),
          cursor: canWrite ? "grab" : "pointer",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "flex-start" }}>
          <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 13, lineHeight: 1.3, flex: 1 }}>
            {task.title}
          </div>
          <TmPriorityPill priority={task.priority} />
        </div>
        {task.projectName && (
          <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ display: "inline-flex", width: 12, height: 12 }} aria-hidden>
              <Ic.book />
            </span>
            {task.projectName}
          </div>
        )}
        {task.tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>
            {task.tags.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                style={{
                  background: tag.color ? `color-mix(in srgb, ${tag.color} 18%, var(--surface))` : "var(--surface-muted)",
                  color: tag.color ? "var(--ink)" : "var(--ink-2)",
                  border: `1px solid ${tag.color ?? "var(--border)"}`,
                  padding: "1px 6px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                {tag.name}
              </span>
            ))}
            {task.tags.length > 3 && (
              <span style={{ color: "var(--ink-4)", fontSize: 10 }}>+{task.tags.length - 3}</span>
            )}
          </div>
        )}
        {task.openPredecessorsCount > 0 && task.status !== "DONE" && task.status !== "CANCELLED" && (
          <div
            style={{
              marginTop: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              alignSelf: "flex-start",
              padding: "1px 6px",
              borderRadius: 4,
              background: "var(--err-bg)",
              color: "var(--err)",
              fontSize: 10,
              fontWeight: 600,
            }}
            title={t("tm.tasks.blockedByTitle", { defaultValue: "Blocked by {{count}} open predecessor task(s)", count: task.openPredecessorsCount })}
          >
            ⛔ {t("tm.tasks.blockedBy", { defaultValue: "Blocked by {{count}}", count: task.openPredecessorsCount })}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, gap: 6 }}>
          <span style={{ fontSize: 11, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {task.assigneeName}
          </span>
          {task.dueDate && (
            <span
              style={{
                fontSize: 11,
                color: overdue ? "var(--err)" : "var(--ink-3)",
                fontWeight: overdue ? 600 : 400,
                whiteSpace: "nowrap",
              }}
              title={overdue ? t("tm.tasks.detail.overdue", "Overdue") : t("tm.tasks.detail.due", "Due")}
            >
              {fmtDue(task.dueDate, today, t)}
            </span>
          )}
        </div>
        {task.blockedReason && (
          <div style={blockedBadge} title={task.blockedReason}>⚠ {task.blockedReason}</div>
        )}
      </article>
    </Link>
  );
}

function TaskList({
  tasks, today, selectedIds, onToggle, onToggleAll,
}: {
  tasks: TmTaskView[];
  today: Date;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}) {
  const { t } = useTranslation();
  const allSelected = tasks.length > 0 && tasks.every((tk) => selectedIds.has(tk.id));
  const someSelected = tasks.some((tk) => selectedIds.has(tk.id));
  return (
    <div>
      <label style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "6px 12px", fontSize: 12, color: "var(--ink-3)", cursor: "pointer",
      }}>
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
          onChange={onToggleAll}
        />
        {t("tm.tasks.selectAllVisible", "Select all visible")}
      </label>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        {tasks.map((task) => {
          const overdue =
            !!task.dueDate
            && new Date(task.dueDate) < today
            && task.status !== "DONE"
            && task.status !== "CANCELLED";
          const selected = selectedIds.has(task.id);
          return (
            <li
              key={task.id}
              className="card"
              style={{
                padding: 12,
                ...(overdue ? { boxShadow: "inset 3px 0 0 var(--err), var(--shadow-1)" } : {}),
                ...(selected ? { background: "var(--brand-primary-50)", borderColor: "var(--brand-primary)" } : {}),
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggle(task.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginRight: 2 }}
                  aria-label={t("tm.tasks.selectTask", { defaultValue: "Select {{title}}", title: task.title })}
                />
                <Link to={`/tm/tasks/${task.id}`} style={{ flex: 1, color: "var(--ink)", textDecoration: "none", minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{task.title}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                    {task.assigneeName}
                    {task.projectName ? ` · ${task.projectName}` : ""}
                    {task.dueDate ? ` · ${overdue ? t("tm.tasks.row.overdueDue", "Overdue, due ") : t("tm.tasks.row.due", "Due ")}${fmtDue(task.dueDate, today, t)}` : ""}
                    {task.blockedReason ? ` · ⚠ ${task.blockedReason}` : ""}
                  </div>
                </Link>
                <TmPriorityPill priority={task.priority} />
                <TmStatusBadge status={task.status} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function KpiTile({
  label, value, accent, bg, onClick, active, danger,
}: {
  label: string; value: number; accent: string; bg: string;
  onClick?: () => void; active?: boolean; danger?: boolean;
}) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      role={clickable ? "button" : undefined}
      style={{
        background: "var(--surface)",
        border: `1px solid ${active ? accent : "var(--border)"}`,
        boxShadow: active ? `0 0 0 3px ${bg}` : "var(--shadow-1)",
        borderRadius: 10,
        padding: "12px 14px",
        cursor: clickable ? "pointer" : "default",
        transition: "border-color .15s ease, box-shadow .15s ease",
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{
        fontSize: 24,
        fontWeight: 700,
        color: danger && value > 0 ? "var(--err)" : "var(--ink)",
        marginTop: 4,
        fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </div>
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "var(--ink-3)" }}>{children}</div>;
}

function priorityChip(p: TmTaskPriority, active: boolean): React.CSSProperties {
  const palette = priorityPalette(p);
  return {
    background: active ? palette.bg : "transparent",
    color: active ? palette.fg : "var(--ink-3)",
    border: `1px solid ${active ? palette.fg : "var(--border)"}`,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 600,
    cursor: "pointer",
    textTransform: "capitalize",
  };
}

function priorityPalette(p: TmTaskPriority): { bg: string; fg: string } {
  switch (p) {
    case "URGENT": return { bg: "var(--err-bg)", fg: "var(--err)" };
    case "HIGH":   return { bg: "var(--warn-bg)", fg: "var(--warn)" };
    case "MEDIUM": return { bg: "var(--info-bg)", fg: "var(--info)" };
    case "LOW":    return { bg: "var(--surface-muted)", fg: "var(--ink-3)" };
  }
}

function priorityLabel(p: TmTaskPriority): string {
  return p.charAt(0) + p.slice(1).toLowerCase();
}

/**
 * Build a CSV from the currently filtered tasks and trigger a browser
 * download. RFC-4180 quoting: wrap every value in double quotes and escape
 * any double quote inside a value by doubling it. Excel happily eats this
 * with Latin-1 characters intact thanks to the UTF-8 BOM.
 */
function downloadTasksCsv(tasks: TmTaskView[], t: TFunction): void {
  const headers = [
    t("tm.tasks.csv.title", "Title"),
    t("tm.tasks.csv.status", "Status"),
    t("tm.tasks.csv.priority", "Priority"),
    t("tm.tasks.csv.assignee", "Assignee"),
    t("tm.tasks.csv.validator", "Validator"),
    t("tm.tasks.csv.project", "Project"),
    t("tm.tasks.csv.dueDate", "Due date"),
    t("tm.tasks.csv.tags", "Tags"),
    t("tm.tasks.csv.createdAt", "Created at"),
    t("tm.tasks.csv.updatedAt", "Updated at"),
  ];
  const rows = tasks.map((t) => [
    t.title,
    t.status,
    t.priority,
    t.assigneeName,
    t.validatorName ?? "",
    t.projectName ?? "",
    t.dueDate ?? "",
    t.tags.map((tg) => tg.name).join(", "),
    t.createdAt,
    t.updatedAt,
  ]);
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\r\n");
  const blob = new Blob(["﻿", lines], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tasks-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function humanStatus(s: TmTaskStatus): string {
  switch (s) {
    case "TODO":        return "To do";
    case "IN_PROGRESS": return "In progress";
    case "IN_REVIEW":   return "In review";
    case "BLOCKED":     return "Blocked";
    case "DONE":        return "Done";
    case "CANCELLED":   return "Cancelled";
  }
}

function tabLabel(t: Tab): string {
  if (t === "mine") return "My tasks";
  if (t === "assigned") return "Assigned by me";
  return "For validation";
}

function fmtDue(iso: string, today: Date, t: TFunction): string {
  const d = new Date(iso);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return t("tm.tasks.due.today", "today");
  if (diff === 1) return t("tm.tasks.due.tomorrow", "tomorrow");
  if (diff === -1) return t("tm.tasks.due.yesterday", "yesterday");
  if (diff < 0) return t("tm.tasks.due.daysAgo", { defaultValue: "{{count}}d ago", count: Math.abs(diff) });
  if (diff < 7) return t("tm.tasks.due.inDays", { defaultValue: "in {{count}}d", count: diff });
  return formatDateShort(d);
}

const tabBar: React.CSSProperties = { display: "flex", gap: 8, borderBottom: "1px solid var(--border)" };
const tabCount: React.CSSProperties = { marginLeft: 6, padding: "1px 7px", borderRadius: 999, background: "var(--surface-2)", fontSize: 11, fontWeight: 600 };
const kpiGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 };
const filterBar: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" };
const searchWrap: React.CSSProperties = { position: "relative", flex: "1 1 260px", display: "flex", alignItems: "center" };
const searchIcon: React.CSSProperties = { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, display: "inline-flex", color: "var(--ink-3)", pointerEvents: "none" };
const kanbanGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, overflowX: "auto" };
const kanbanCol: React.CSSProperties = { background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10, padding: 10, minHeight: 200, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 };
const kanbanHead: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 6, borderBottom: "1px solid var(--border)" };
const kanbanList: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 8, flex: 1 };
const taskCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-1)", transition: "transform .12s ease, box-shadow .12s ease" };
const emptyCol: React.CSSProperties = { color: "var(--ink-4)", fontSize: 12, textAlign: "center", padding: "16px 0" };
const blockedBadge: React.CSSProperties = { marginTop: 6, fontSize: 11, padding: "3px 6px", background: "var(--err-bg)", color: "var(--err)", borderRadius: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const emptyState: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
const bulkBar: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  padding: "10px 12px",
  background: "var(--brand-primary-50)",
  border: "1px solid var(--brand-primary)",
  borderRadius: 10,
  fontSize: 13,
};
